import { Skeleton, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import React, { useMemo, useState } from "react";

const CHART_WIDTH = 1000;
const CHART_HEIGHT = 140;
const PADDING = { top: 14, right: 16, bottom: 28, left: 32 };

const RANGE_BY_GRANULARITY = {
  day: [
    { value: 7, label: "7D" },
    { value: 14, label: "14D" },
    { value: 30, label: "30D" },
  ],
  month: [
    { value: 6, label: "6M" },
    { value: 12, label: "12M" },
  ],
  year: [
    { value: 3, label: "3Y" },
    { value: 5, label: "5Y" },
  ],
};

function startOfDay(date) {
  const value = new Date(date);
  value.setHours(0, 0, 0, 0);
  return value;
}

function dayKey(date) {
  const value = startOfDay(date);
  return `${value.getFullYear()}-${value.getMonth()}-${value.getDate()}`;
}

function monthKey(date) {
  const value = new Date(date);
  return `${value.getFullYear()}-${value.getMonth()}`;
}

function yearKey(date) {
  return String(new Date(date).getFullYear());
}

export function buildPurchaseChartData(
  records,
  granularity = "month",
  rangeCount = 6,
  chartDays
) {
  const now = new Date();
  const buckets = [];

  if (granularity === "day") {
    for (let index = rangeCount - 1; index >= 0; index -= 1) {
      const date = startOfDay(now);
      date.setDate(date.getDate() - index);
      buckets.push({
        key: dayKey(date),
        label: date.toLocaleDateString("en-IN", {
          day: "numeric",
          month: "short",
        }),
        count: 0,
      });
    }
  } else if (granularity === "year") {
    for (let index = rangeCount - 1; index >= 0; index -= 1) {
      const year = now.getFullYear() - index;
      buckets.push({
        key: String(year),
        label: String(year),
        count: 0,
      });
    }
  } else {
    for (let index = rangeCount - 1; index >= 0; index -= 1) {
      const date = new Date(now.getFullYear(), now.getMonth() - index, 1);
      buckets.push({
        key: monthKey(date),
        label: date.toLocaleDateString("en-IN", {
          month: "short",
          year: "2-digit",
        }),
        count: 0,
      });
    }
  }

  const bucketMap = Object.fromEntries(buckets.map((bucket) => [bucket.key, bucket]));

  if (Array.isArray(chartDays) && chartDays.length) {
    chartDays.forEach((entry) => {
      const paymentDate = new Date(entry.year, entry.month - 1, entry.day);
      if (Number.isNaN(paymentDate.getTime())) return;

      let key;
      if (granularity === "day") {
        key = dayKey(paymentDate);
      } else if (granularity === "year") {
        key = yearKey(paymentDate);
      } else {
        key = monthKey(paymentDate);
      }

      if (bucketMap[key]) {
        bucketMap[key].count += Number(entry.count) || 0;
      }
    });
  } else {
    (Array.isArray(records) ? records : []).forEach((record) => {
      const paymentDate = new Date(record.paymentDate);
      if (Number.isNaN(paymentDate.getTime())) return;

      let key;
      if (granularity === "day") {
        key = dayKey(paymentDate);
      } else if (granularity === "year") {
        key = yearKey(paymentDate);
      } else {
        key = monthKey(paymentDate);
      }

      if (bucketMap[key]) {
        bucketMap[key].count += 1;
      }
    });
  }

  return buckets;
}

function buildLineGeometry(chartData, maxCount) {
  const plotWidth = CHART_WIDTH - PADDING.left - PADDING.right;
  const plotHeight = CHART_HEIGHT - PADDING.top - PADDING.bottom;
  const bottomY = PADDING.top + plotHeight;
  const stepX = chartData.length > 1 ? plotWidth / (chartData.length - 1) : 0;

  const points = chartData.map((point, index) => {
    const x = PADDING.left + index * stepX;
    const y =
      PADDING.top + plotHeight - (point.count / maxCount) * plotHeight;
    return { ...point, x, y };
  });

  const linePath = points
    .map((point, index) => `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`)
    .join(" ");

  const areaPath = points.length
    ? `${linePath} L ${points[points.length - 1].x} ${bottomY} L ${points[0].x} ${bottomY} Z`
    : "";

  const yTicks = [0, Math.ceil(maxCount / 2), maxCount].filter(
    (value, index, array) => array.indexOf(value) === index
  );

  const gridLines = yTicks.map((tick) => {
    const y = PADDING.top + plotHeight - (tick / maxCount) * plotHeight;
    return { tick, y };
  });

  return { points, linePath, areaPath, gridLines, bottomY, plotWidth };
}

function getSummaryLabel(granularity, rangeCount) {
  if (granularity === "day") {
    return `last ${rangeCount} day${rangeCount === 1 ? "" : "s"}`;
  }
  if (granularity === "year") {
    return `last ${rangeCount} year${rangeCount === 1 ? "" : "s"}`;
  }
  return `last ${rangeCount} month${rangeCount === 1 ? "" : "s"}`;
}

function shouldShowXLabel(index, total, granularity) {
  if (total <= 12) return true;
  if (granularity === "day") return index === 0 || index === total - 1 || index % 5 === 0;
  return index % 2 === 0;
}

function SubscriptionPurchasesChart({ records, chartDays, loading }) {
  const [granularity, setGranularity] = useState("month");
  const [range, setRange] = useState(6);
  const [activeIndex, setActiveIndex] = useState(null);

  const rangeOptions = RANGE_BY_GRANULARITY[granularity];

  const chartData = useMemo(
    () => buildPurchaseChartData(records, granularity, range, chartDays),
    [records, chartDays, granularity, range]
  );

  const maxCount = Math.max(...chartData.map((point) => point.count), 1);
  const totalInRange = chartData.reduce((sum, point) => sum + point.count, 0);
  const geometry = useMemo(
    () => buildLineGeometry(chartData, maxCount),
    [chartData, maxCount]
  );

  const handleGranularityChange = (_, value) => {
    if (!value) return;
    setGranularity(value);
    setRange(RANGE_BY_GRANULARITY[value][0].value);
    setActiveIndex(null);
  };

  const handleRangeChange = (_, value) => {
    if (!value) return;
    setRange(value);
    setActiveIndex(null);
  };

  if (loading) {
    return (
      <div className="dash-purchase-chart">
        <Skeleton variant="rounded" height={150} />
      </div>
    );
  }

  return (
    <div className="dash-purchase-chart">
      <div className="dash-purchase-chart-toolbar">
        <Typography className="dash-purchase-chart-summary">
          <strong>{totalInRange}</strong> purchase{totalInRange === 1 ? "" : "s"} in the{" "}
          {getSummaryLabel(granularity, range)}
        </Typography>
        <div className="dash-purchase-chart-filters">
          <ToggleButtonGroup
            size="small"
            exclusive
            value={granularity}
            onChange={handleGranularityChange}
            className="dash-purchase-range-toggle"
          >
            <ToggleButton value="day">Day</ToggleButton>
            <ToggleButton value="month">Month</ToggleButton>
            <ToggleButton value="year">Year</ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={range}
            onChange={handleRangeChange}
            className="dash-purchase-range-toggle"
          >
            {rangeOptions.map((option) => (
              <ToggleButton key={option.value} value={option.value}>
                {option.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </div>
      </div>

      {totalInRange === 0 ? (
        <div className="dash-purchase-chart-empty">
          <Typography color="text.secondary">
            No subscription purchases in this period yet.
          </Typography>
        </div>
      ) : (
        <div className="dash-line-chart-wrap">
          <svg
            className="dash-line-chart"
            viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
            role="img"
            aria-label={`Subscription purchases by ${granularity} line chart`}
          >
            <defs>
              <linearGradient id="purchaseLineFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.28" />
                <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.02" />
              </linearGradient>
            </defs>

            {geometry.gridLines.map((line) => (
              <g key={line.tick}>
                <line
                  x1={PADDING.left}
                  x2={CHART_WIDTH - PADDING.right}
                  y1={line.y}
                  y2={line.y}
                  className="dash-line-chart-grid"
                />
                <text
                  x={PADDING.left - 8}
                  y={line.y + 4}
                  className="dash-line-chart-y-label"
                  textAnchor="end"
                >
                  {line.tick}
                </text>
              </g>
            ))}

            <path d={geometry.areaPath} fill="url(#purchaseLineFill)" />
            <path d={geometry.linePath} className="dash-line-chart-line" />

            {geometry.points.map((point, index) => (
              <g
                key={point.key}
                onMouseEnter={() => setActiveIndex(index)}
                onMouseLeave={() => setActiveIndex(null)}
              >
                <circle
                  cx={point.x}
                  cy={point.y}
                  r={activeIndex === index ? 5 : 3.5}
                  className={`dash-line-chart-point ${
                    activeIndex === index ? "is-active" : ""
                  }`}
                />
                {shouldShowXLabel(index, geometry.points.length, granularity) ? (
                  <text
                    x={point.x}
                    y={CHART_HEIGHT - 10}
                    className="dash-line-chart-x-label"
                    textAnchor="middle"
                  >
                    {point.label}
                  </text>
                ) : null}
              </g>
            ))}
          </svg>

          {activeIndex !== null && geometry.points[activeIndex] ? (
            <div className="dash-line-chart-tooltip">
              <Typography className="dash-line-chart-tooltip-label">
                {geometry.points[activeIndex].label}
              </Typography>
              <Typography className="dash-line-chart-tooltip-value">
                {geometry.points[activeIndex].count} purchase
                {geometry.points[activeIndex].count === 1 ? "" : "s"}
              </Typography>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

export default SubscriptionPurchasesChart;
